import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button,
    Snackbar, Alert, Paper, Grid, CircularProgress,
    Divider, Stack, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
    Timeline, TimelineItem, TimelineSeparator,
    TimelineConnector, TimelineContent, TimelineDot,
    TimelineOppositeContent
} from '@mui/lab';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ticketService, configService } from '../api';

const statusColors = {
    NEW: 'info',
    OPEN: 'primary',
    IN_PROGRESS: 'warning',
    RESOLVED: 'success',
    CLOSED: 'default',
};

const priorityColors = {
    HIGH: 'error',
    MEDIUM: 'warning',
    LOW: 'success',
};

const ExternalTicketFollowUp = () => {
    const [ticketId, setTicketId] = useState('');
    const [email, setEmail] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [tickets, setTickets] = useState([]); // Lista de resultados
    const [selectedTicket, setSelectedTicket] = useState(null); // Detalle individual
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [errors, setErrors] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const res = await configService.getStatuses();
                setStatuses(res.data.results || res.data);
            } catch (err) {
                console.error("Error al cargar estados:", err);
            }
        };
        fetchStatuses();
    }, []);

    const validate = () => {
        let tempErrors = {};
        // El ID ahora es opcional para permitir búsqueda masiva por Email
        tempErrors.email = (/\S+@\S+\.\S+/).test(email) ? "" : "El correo electrónico no es válido.";
        setErrors(tempErrors);
        return Object.values(tempErrors).every(x => x === "");
    };

    const getStatusChip = (sKey) => {
        const config = statuses.find(s => s.key === sKey);
        if (config) {
            return (
                <Chip 
                    label={config.name} 
                    sx={{ bgcolor: config.color, color: '#fff', fontWeight: 'bold' }} 
                    size="small" 
                />
            );
        }
        return <Chip label={sKey} color={statusColors[sKey] || 'default'} size="small" />;
    };

    const handleFollowUp = async (e) => {
        e.preventDefault();
        if (validate()) {
            setLoading(true);
            setTickets([]);
            setSelectedTicket(null);
            setSearchPerformed(false);
            try {
                const response = await ticketService.followUpExternalTicket(ticketId, email, {
                    status: filterStatus,
                    start_date: startDate,
                    end_date: endDate
                });
                const data = response.data;
                
                if (Array.isArray(data)) { 
                    setTickets(data);
                } else { 
                    setTickets([data]);
                    setSelectedTicket(data);
                }
                setSnackbarOpen(false);
                setSearchPerformed(true);
            } catch (err) {
                let errorMessage = "No se pudo recuperar la información. Verifique sus datos.";
                if (err.response) {
                    if (err.response.status < 500 && err.response.data) {
                        const backendMsg = err.response.data.error || err.response.data.detail;
                        errorMessage = backendMsg || errorMessage;
                    } else if (err.response.status >= 500) {
                        errorMessage = "Error interno del servidor. Por favor, intente más tarde.";
                    }
                }
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                setSearchPerformed(true);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
            <Paper sx={{ p: 4, mb: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight="bold">
                    Seguimiento de Tickets
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                    Ingrese su correo electrónico para ver todos sus tickets o incluya el ID para uno específico.
                </Typography>

                <form onSubmit={handleFollowUp}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="ID del Ticket (Opcional)"
                                name="ticketId"
                                value={ticketId}
                                onChange={(e) => setTicketId(e.target.value)}
                                error={!!errors.ticketId}
                                helperText={errors.ticketId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tu Correo Electrónico"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={!!errors.email}
                                helperText={errors.email}
                                type="email"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Estado"
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <MenuItem value="ALL">Todos los Estados</MenuItem>
                                    {statuses.map(s => (
                                        <MenuItem key={s.id} value={s.key}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Desde"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Hasta"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Consultar'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Mensaje cuando no se encuentran resultados */}
            {searchPerformed && tickets.length === 0 && !loading && !selectedTicket && (
                <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                        No se encontraron tickets asociados a este correo electrónico.
                    </Typography>
                </Paper>
            )}

            {/* Lista de Tickets encontrados */}
            {tickets.length > 0 && !selectedTicket && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                            <TableRow>
                                <TableCell>Asunto</TableCell>
                                <TableCell>Fecha de Creación</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Resolución Esperada</TableCell>
                                <TableCell align="right">Acción</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t.title}</TableCell>
                                    <TableCell>{new Date(t.created_at).toLocaleString()}</TableCell>
                                    <TableCell>{getStatusChip(t.status)}</TableCell>
                                    <TableCell sx={{ color: 'error.main', fontWeight: '500' }}>
                                        {new Date(t.expected_resolution_date).toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button variant="outlined" size="small" onClick={() => setSelectedTicket(t)}>
                                            Ver Detalle
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Detalle del Ticket Seleccionado */}
            {selectedTicket && (
                <Box sx={{ mt: 3 }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => setSelectedTicket(null)} 
                        sx={{ mb: 2 }}
                    >
                        Volver a la lista
                    </Button>
                    <Paper sx={{ p: 4 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                            Ticket #{selectedTicket.id}: {selectedTicket.title}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2">Descripción</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                                    {selectedTicket.description}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Estado</Typography>
                                        <Box>{getStatusChip(selectedTicket.status)}</Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Prioridad</Typography>
                                        <Box><Chip label={selectedTicket.priority} color={priorityColors[selectedTicket.priority]} size="small" /></Box>
                                    </Box>
                                </Stack>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Enviado por</Typography>
                                        <Typography variant="body2">{selectedTicket.display_submitter_email}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Asignado a</Typography>
                                        <Typography variant="body2">{selectedTicket.assigned_to_email || 'Sin Asignar'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Fecha de Creación</Typography>
                                        <Typography variant="body2">{new Date(selectedTicket.created_at).toLocaleString()}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Resolución Esperada</Typography>
                                        <Typography variant="body2" color="error.main">
                                            {new Date(selectedTicket.expected_resolution_date).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                    <HistoryIcon />
                                    <Typography variant="h6">Historial del Ticket</Typography>
                                </Stack>
                                <Timeline position="right" sx={{ p: 0 }}>
                                    {selectedTicket.history?.map((entry, index) => (
                                        <TimelineItem key={entry.id}>
                                            <TimelineOppositeContent color="textSecondary" sx={{ fontSize: '0.75rem', flex: 0.2 }}>
                                                {new Date(entry.changed_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                            </TimelineOppositeContent>
                                            <TimelineSeparator>
                                                <TimelineDot color="primary" variant="outlined" />
                                                {index < selectedTicket.history.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent sx={{ py: '12px', px: 2 }}>
                                                <Typography variant="subtitle2" component="span">{entry.changed_by_email || 'Sistema'}</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Actualizó <strong>{entry.field_changed}</strong>
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                    {entry.old_value || 'Ninguno'} → {entry.new_value}
                                                </Typography>
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Timeline>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExternalTicketFollowUp;